import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Users, Calendar, Filter, User, Mail, Phone, MapPin, Ship, Award } from 'lucide-react';
import { toast } from 'sonner';
import NotificationApiService from '../../../api/notificationApi';

interface GroupDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
}

interface GroupMember {
  id: string;
  name?: string;
  email: string;
  role?: string;
  // Additional fields that might be present
  firstName?: string;
  lastName?: string;
  indianCdc?: string;
  currentShipImo?: string;
  rankDesignation?: string;
  age?: number;
  gender?: string;
  mobileNumber?: string;
  currentShipName?: string;
  lastLoginAt?: string;
}

export default function GroupDetailsModal({ isOpen, onClose, groupId }: GroupDetailsModalProps) {
  const [group, setGroup] = useState<any>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  useEffect(() => {
    if (isOpen && groupId) {
      fetchGroupDetails();
      fetchGroupMembers();
    }
  }, [isOpen, groupId]);

  const fetchGroupDetails = async () => {
    setIsLoading(true);
    try {
      const response = await NotificationApiService.getGroup(groupId);
      if (response.success) {
        setGroup(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch group details');
      }
    } catch (error) {
      console.error('Error fetching group details:', error);
      toast.error('Failed to fetch group details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGroupMembers = async () => {
    setIsLoadingMembers(true);
    try {
      if (group?.filters) {
        const usersResponse = await NotificationApiService.previewUsers(group.filters);
        if (usersResponse.success && usersResponse.data) {
          setMembers(usersResponse.data.usersPreview);
        } else {
          toast.error(usersResponse.message || 'Failed to fetch group members');
        }
      } else {
        setMembers([]);
      }
    } catch (error) {
      console.error('Error fetching group members:', error);
      toast.error('Failed to fetch group members');
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilterSummary = (filters: any) => {
    const summary = [];
    
    if (filters.roles?.length > 0) {
      summary.push(`Roles: ${filters.roles.join(', ')}`);
    }
    if (filters.ranks?.length > 0) {
      summary.push(`Ranks: ${filters.ranks.join(', ')}`);
    }
    if (filters.shipNames?.length > 0) {
      summary.push(`Ships: ${filters.shipNames.join(', ')}`);
    }
    if (filters.gender?.length > 0) {
      summary.push(`Gender: ${filters.gender.join(', ')}`);
    }
    if (filters.ageRange) {
      if (filters.ageRange.min && filters.ageRange.max) {
        summary.push(`Age: ${filters.ageRange.min}-${filters.ageRange.max}`);
      } else if (filters.ageRange.min) {
        summary.push(`Age: ${filters.ageRange.min}+`);
      } else if (filters.ageRange.max) {
        summary.push(`Age: Up to ${filters.ageRange.max}`);
      }
    }
    if (filters.lastLoginDays) {
      summary.push(`Active: Last ${filters.lastLoginDays} days`);
    }
    if (filters.customQuery) {
      summary.push(`Custom: ${filters.customQuery}`);
    }
    
    return summary.length > 0 ? summary.join(' • ') : 'No specific filters';
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Group Details: {group?.name || 'Loading...'}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : group ? (
          <div className="space-y-6">
            {/* Group Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Group Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Group Name</label>
                    <p className="text-lg font-semibold">{group.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <div className="mt-1">
                      <Badge variant={group.isActive ? "default" : "secondary"}>
                        {group.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Total Members</label>
                    <p className="text-lg font-semibold">{members.length}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Created</label>
                    <p className="text-sm">{formatDate(group.createdAt)}</p>
                  </div>
                </div>
                
                {group.description && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    <p className="text-sm mt-1">{group.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Filter Criteria */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Filter Criteria
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm">{getFilterSummary(group.filters)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Group Members */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Group Members ({members.length})
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchGroupMembers}
                    disabled={isLoadingMembers}
                  >
                    {isLoadingMembers ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    ) : (
                      'Refresh'
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingMembers ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : members.length > 0 ? (
                  <div className="space-y-3">
                    {members.map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium">
                              {member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown User'}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-4">
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {member.email}
                              </span>
                              {member.indianCdc && (
                                <span className="flex items-center gap-1">
                                  <Award className="h-3 w-3" />
                                  {member.indianCdc}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {member.role && (
                            <Badge variant="outline">{member.role}</Badge>
                          )}
                          {member.rankDesignation && (
                            <Badge variant="outline">{member.rankDesignation}</Badge>
                          )}
                          {member.currentShipName && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Ship className="h-3 w-3" />
                              {member.currentShipName}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No members found</p>
                    <p className="text-sm">This group may not have any users matching the criteria</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Last Updated */}
            <div className="text-xs text-muted-foreground text-center">
              Last updated: {formatDate(group.updatedAt)}
            </div>
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Group not found</p>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 